import { Stack, FormGroup, FormRow } from '@cad-arc';
import { ConsolidatedLocationView, EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import React from 'react';
import {
    AgencyFieldsDisplayRow,
    AgencyFormGroupEditable,
} from '../../panels/entity-panels/entity-panel-components';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { globalAttributes } from '../../common/global-attributes';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { FormSelect } from '../../forms/integrated-form-controls/form-select';
import { SubdivisionFields } from './event-panel-form-subdivision-fields';
import { useAgencyOptions } from './use-agency-options';

type EventPanelFormEmsFieldsProps = {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
};

function useValuesFromProps(props: EventPanelFormEmsFieldsProps) {
    const callForServiceValue = useWatch({
        control: props.form.control,
        name: 'callForService',
    });

    const location = useWatch({
        control: props.form.control,
        name: 'location',
        exact: true,
    }) as ConsolidatedLocationView | null;

    const agencyOptions = useAgencyOptions({
        location,
        baseOptions: props.eventPanelOptions.emsAgencyProfileOptions,
        agencyType: globalAttributes.agencyTypeGlobal.EMS,
    });

    const eventForm = props.form;
    const eventPanelOptions = props.eventPanelOptions;

    const cfsIncludesEms = callForServiceValue?.agencyTypeGlobalAttrIds.includes(
        globalAttributes.agencyTypeGlobal.EMS
    );
    return {
        eventForm,
        eventPanelOptions,
        agencyOptions,
        callForServiceValue,
        cfsIncludesEms,
    };
}

function DAreaAgencyFields({
    asDataListItem,
    ...props
}: EventPanelFormEmsFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const localProps = useValuesFromProps({ ...props });

    return (
        <>
            <FormSelect
                asEditable={false}
                asDataListItem={asDataListItem}
                length="sm"
                control={localProps.eventForm.control}
                label="D-Area"
                name="emsDispatchArea"
                eventNotificationName="DISPATCH_AREA"
                options={props.eventPanelOptions.emsDispatchAreaOptions}
                showRequiredIndicator={!!localProps.cfsIncludesEms}
            />
            <FormSelect
                asEditable={false}
                asDataListItem={asDataListItem}
                control={localProps.eventForm.control}
                label="Agency"
                name="emsAgency"
                eventNotificationName="AGENCY"
                options={localProps.agencyOptions}
                showRequiredIndicator={!!localProps.cfsIncludesEms}
                length="md"
            />
        </>
    );
}

function RadioField({
    asDataListItem,
    ...props
}: EventPanelFormEmsFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const localProps = useValuesFromProps({ ...props });

    if (enabledFeatures.radioChannels) {
        return (
            <FormSelect
                asEditable={false}
                asDataListItem={asDataListItem}
                control={localProps.eventForm.control}
                label="Primary Radio Channel"
                name="emsPrimaryRadioChannel"
                options={props.eventPanelOptions.radioChannelOptions}
                length="md"
            />
        );
    }
    return <></>;
}

function EmsFields({
    asDataListItem,
    ...props
}: EventPanelFormEmsFieldsProps & { asDataListItem?: boolean }): React.ReactElement {
    const localProps = useValuesFromProps({ ...props });

    return asDataListItem ? (
        <Stack spacing={0}>
            <AgencyFieldsDisplayRow>
                <SubdivisionFields
                    asDataListItem
                    eventClassPrefix="ems"
                    form={localProps.eventForm}
                />
            </AgencyFieldsDisplayRow>
            <AgencyFieldsDisplayRow>
                <DAreaAgencyFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            </AgencyFieldsDisplayRow>
            <AgencyFieldsDisplayRow>
                <RadioField
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            </AgencyFieldsDisplayRow>
        </Stack>
    ) : (
        <FormGroup spacing={1}>
            <FormRow>
                <SubdivisionFields eventClassPrefix="ems" form={localProps.eventForm} />
            </FormRow>
            <FormRow>
                <DAreaAgencyFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            </FormRow>
            <FormRow>
                <RadioField
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            </FormRow>
        </FormGroup>
    );
}

export function EventPanelFormEMSFields(props: {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
}): React.ReactElement {
    const localProps = useValuesFromProps(props);

    return (
        <AgencyFormGroupEditable
            agency="EMS"
            viewMode={
                <EmsFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                    asDataListItem
                />
            }
            editMode={
                <EmsFields
                    form={localProps.eventForm}
                    eventPanelOptions={localProps.eventPanelOptions}
                />
            }
        />
    );
}
